/**
 * Lista estreita de `/admin/cartoes` (busca, fichas de tipo, ações da linha,
 * ação flutuante). Os 88px afastam o botão da BottomTabBar.
 */
import { useRef, useState } from 'react';
import { Popconfirm } from 'antd';
import {
  Brain,
  ChevronRight,
  Pencil,
  Plus,
  ScanEye,
  Search,
  Trash2,
  Type,
} from 'lucide-react';
import type { CardLayout, ReviewCardAdminRow } from '../../../sdk/@types/Revisao';
import { cardTipoMostrado, type CardTipoMostrado } from './cardTipo';
import { accuracyLabel, markingCount, markingLabel, shortIdLabel } from './cartaoLinha';
import DuplicadoBadge from './DuplicadoBadge';
import type { SelecaoLinhas } from './loteSelecao';

export interface FichaTipoEstreita {
  id: CardLayout | 'all';
  label: string;
  count: number | null;
}

export interface CartoesListaEstreitaProps {
  search: string;
  onSearchChange: (value: string) => void;
  types: FichaTipoEstreita[];
  selectedType: CardLayout | 'all';
  onSelectType: (id: CardLayout | 'all') => void;
  /** Matéria, tag e a barra de lote. Render between the type chips and the rows. */
  filters?: React.ReactNode;
  rows: ReviewCardAdminRow[];
  subjectName: (subjectId: string) => string | undefined;
  loading: boolean;
  emptyLabel: string;
  selecao: SelecaoLinhas;
  onEdit: (card: ReviewCardAdminRow) => void;
  onDelete: (id: string) => void;
  onCreate: () => void;
  rangeLabel: string | null;
  showPager: boolean;
  previousDisabled: boolean;
  nextDisabled: boolean;
  onPrevious: () => void;
  onNext: () => void;
}

const BUSCA = 'Buscar por conteúdo, matéria, tag ou número';

const SWIPE_PX = 48;

const PAGER =
  'inline-flex items-center justify-center rounded-input border border-edge bg-surface px-3 py-1.5 text-sm text-ink hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-50';

const EDITAR =
  'inline-flex h-11 w-16 shrink-0 flex-col items-center justify-center gap-0.5 whitespace-nowrap rounded-input border border-edge bg-surface px-1 text-xs font-medium leading-tight text-ink hover:bg-surface-hover';

const EXCLUIR =
  'inline-flex h-11 w-16 shrink-0 flex-col items-center justify-center gap-0.5 whitespace-nowrap rounded-input border border-danger/40 bg-surface px-1 text-xs font-medium leading-tight text-danger-ink hover:bg-danger/[0.08]';

const INTERACTIVE = 'button, a, input, label, textarea, select';

const TIPO_TILE: Record<CardTipoMostrado, { Icon: typeof Type; tile: string }> = {
  Texto: { Icon: Type, tile: 'bg-accent-blue/[0.12] text-accent-blue-ink' },
  Oclusão: { Icon: ScanEye, tile: 'bg-accent-purple/[0.12] text-accent-purple-ink' },
  Memorizar: { Icon: Brain, tile: 'bg-accent-green/[0.12] text-accent-green-ink' },
};

function textoFrente(card: ReviewCardAdminRow): string {
  const trimmed = card.front?.trim() ?? '';
  return trimmed || '—';
}

/** Identificador falado da linha: o número curto, ou a frente quando ele falta. */
function nomeDaLinha(card: ReviewCardAdminRow): string {
  return shortIdLabel(card.shortId) ?? textoFrente(card);
}

function isInteractive(target: EventTarget | null): boolean {
  return target instanceof Element && Boolean(target.closest(INTERACTIVE));
}

function capturePointer(target: HTMLElement, pointerId: number) {
  if (typeof target.setPointerCapture !== 'function') return;
  try {
    target.setPointerCapture(pointerId);
  } catch {
    // jsdom não prende o ponteiro; o pointerup ainda resolve se soltar na linha.
  }
}

interface LinhaEstreitaProps {
  card: ReviewCardAdminRow;
  selected: boolean;
  open: boolean;
  subjectName: CartoesListaEstreitaProps['subjectName'];
  selecao: SelecaoLinhas;
  onToggleOpen: () => void;
  onOpen: () => void;
  onClose: () => void;
  onEdit: CartoesListaEstreitaProps['onEdit'];
  onDelete: CartoesListaEstreitaProps['onDelete'];
}

function LinhaEstreita({
  card,
  selected,
  open,
  subjectName,
  selecao,
  onToggleOpen,
  onOpen,
  onClose,
  onEdit,
  onDelete,
}: LinhaEstreitaProps) {
  const chevronRef = useRef<HTMLButtonElement>(null);
  const ignoreChevronClick = useRef(false);
  const gesture = useRef<{ id: number; x: number; y: number } | null>(null);

  const tipo = cardTipoMostrado(card);
  const tile = TIPO_TILE[tipo];
  const TileIcon = tile.Icon;
  const frente = textoFrente(card);
  const nome = nomeDaLinha(card);
  const acerto = accuracyLabel(card);
  const curto = shortIdLabel(card.shortId);
  const marcas = markingCount(card);
  const materia = subjectName(card.subjectId) || '—';
  const tags = card.tags ?? [];

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    if (isInteractive(event.target)) return;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const current = gesture.current;
    if (!current || current.id !== event.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    capturePointer(event.currentTarget, event.pointerId);
    if (event.pointerType === 'mouse') event.preventDefault();
  };

  const onPointerUp = (event: React.PointerEvent<HTMLElement>) => {
    const current = gesture.current;
    gesture.current = null;
    if (!current || current.id !== event.pointerId) return;
    const dx = event.clientX - current.x;
    const dy = event.clientY - current.y;
    if (Math.abs(dx) <= Math.abs(dy)) return;
    const abre = dx <= -SWIPE_PX;
    const fecha = dx >= SWIPE_PX;
    if (!abre && !fecha) return;
    if (event.target instanceof Node && chevronRef.current?.contains(event.target)) {
      ignoreChevronClick.current = true;
    }
    if (abre) onOpen();
    else onClose();
  };

  const onPointerCancel = () => {
    gesture.current = null;
  };

  return (
    <article
      className={`relative flex min-h-[76px] touch-pan-y items-center gap-3 overflow-hidden rounded-card border border-edge px-3 py-3 ${
        selected ? 'bg-select/[0.14]' : 'bg-surface'
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      <label
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={selecao.selectedIds.has(card.id)}
          onChange={() => selecao.onToggle(card.id)}
          onClick={(event) => event.stopPropagation()}
          aria-label={`Selecionar ${nome}`}
          className="h-5 w-5"
        />
      </label>

      <span
        className={`inline-flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-input ${tile.tile}`}
        aria-hidden
      >
        <TileIcon size={24} />
      </span>

      <div className="min-w-0 flex-1">
          <p
            className="line-clamp-2 text-sm font-medium leading-snug text-ink"
            title={frente === '—' ? undefined : frente}
          >
            {frente}
          </p>
          <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5 text-xs text-ink-secondary">
            <span className="shrink-0">{tipo}</span>
            {curto ? (
              <>
                <span aria-hidden className="shrink-0">
                  ·
                </span>
                <span className="shrink-0">{curto}</span>
              </>
            ) : null}
            <span aria-hidden className="shrink-0">
              ·
            </span>
            <span
              className={`shrink-0 ${acerto === 'Indisponível' ? 'text-ink-muted' : 'text-ink'}`}
            >
              {acerto}
            </span>
            {marcas !== null ? (
              <>
                <span aria-hidden className="shrink-0">
                  ·
                </span>
                <span className="truncate">{markingLabel(marcas)}</span>
              </>
            ) : null}
          </p>
          <p className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs text-ink-muted">
            <span className="sr-only">Matéria</span>
            <span className="min-w-0 shrink truncate">{materia}</span>
            <span aria-hidden className="shrink-0">
              ·
            </span>
            <span className="sr-only">Tags</span>
            {tags.length === 0 ? (
              <span className="shrink-0">—</span>
            ) : (
              <span className="flex min-w-0 flex-wrap gap-1">
                {tags.map((tag, index) => (
                  <span
                    key={`${tag}-${index}`}
                    className="shrink-0 rounded-chip bg-surface-muted px-2 py-0.5 text-ink-secondary"
                  >
                    {tag}
                  </span>
                ))}
              </span>
            )}
          </p>
          {card.probableDuplicate ? (
            <div className="mt-1 min-w-0">
              <DuplicadoBadge duplicate={card.probableDuplicate} />
            </div>
          ) : null}
      </div>

      <button
        ref={chevronRef}
        type="button"
        aria-expanded={open}
        aria-pressed={open}
        aria-label={`Ações de ${nome}`}
        onClick={() => {
          if (ignoreChevronClick.current) {
            ignoreChevronClick.current = false;
            return;
          }
          onToggleOpen();
        }}
        className="relative z-20 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-input text-ink-secondary hover:bg-surface-hover"
      >
        <ChevronRight size={20} aria-hidden className={open ? 'rotate-180' : undefined} />
      </button>

      {open ? (
        <div
          className="absolute inset-y-0 right-16 z-10 flex items-center gap-2 bg-surface-muted px-2"
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" className={EDITAR} onClick={() => onEdit(card)}>
            <Pencil size={16} aria-hidden />
            Editar
          </button>
          <Popconfirm
            title="Excluir cartão?"
            description="Remove o template e a cópia de todos os alunos."
            okText="Excluir"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => onDelete(card.id)}
          >
            <button type="button" className={EXCLUIR}>
              <Trash2 size={16} aria-hidden />
              Excluir
            </button>
          </Popconfirm>
        </div>
      ) : null}
    </article>
  );
}

export default function CartoesListaEstreita({
  search,
  onSearchChange,
  types,
  selectedType,
  onSelectType,
  filters,
  rows,
  subjectName,
  loading,
  emptyLabel,
  selecao,
  onEdit,
  onDelete,
  onCreate,
  rangeLabel,
  showPager,
  previousDisabled,
  nextDisabled,
  onPrevious,
  onNext,
}: CartoesListaEstreitaProps): JSX.Element {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="md:hidden flex min-w-0 flex-col gap-3" aria-busy={loading}>
      <div className="relative">
        <Search
          size={18}
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted"
        />
        <input
          type="search"
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={BUSCA}
          aria-label={BUSCA}
          className="h-10 w-full rounded-input border border-edge bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-muted"
        />
      </div>

      <div className="flex min-w-0 flex-wrap gap-2" role="group" aria-label="Tipo">
        {types.map((type) => {
          const selected = selectedType === type.id;
          return (
            <button
              key={type.id}
              type="button"
              aria-pressed={selected}
              onClick={() => onSelectType(type.id)}
              className={`inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-chip border px-3 py-1.5 text-sm ${
                selected
                  ? 'border-select/[0.42] bg-select/[0.14] text-select-ink'
                  : 'border-edge bg-surface text-ink hover:bg-surface-hover'
              }`}
            >
              <span>{type.label}</span>
              <span className="tabular-nums">{type.count === null ? '—' : type.count}</span>
            </button>
          );
        })}
      </div>

      {filters}

      <div className="flex flex-col gap-2 pb-24">
        {rows.length === 0 ? (
          <p className="text-sm text-ink-muted">{loading ? 'Carregando…' : emptyLabel}</p>
        ) : (
          rows.map((card) => (
            <LinhaEstreita
              key={card.id}
              card={card}
              selected={selecao.selectedIds.has(card.id)}
              open={openId === card.id}
              subjectName={subjectName}
              selecao={selecao}
              onToggleOpen={() =>
                setOpenId((current) => (current === card.id ? null : card.id))
              }
              onOpen={() => setOpenId(card.id)}
              onClose={() => setOpenId((current) => (current === card.id ? null : current))}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        )}

        {rangeLabel !== null ? (
          <p className="text-sm text-ink-secondary" aria-live="polite">
            {rangeLabel}
          </p>
        ) : null}
        {showPager ? (
          <div className="flex gap-2">
            <button
              type="button"
              className={PAGER}
              disabled={previousDisabled}
              onClick={onPrevious}
            >
              Anterior
            </button>
            <button type="button" className={PAGER} disabled={nextDisabled} onClick={onNext}>
              Próxima
            </button>
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onCreate}
        aria-label="Novo cartão"
        className="fixed right-4 bottom-[88px] z-20 inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-brand text-brand-contrast md:hidden"
      >
        <Plus size={26} aria-hidden />
      </button>
    </div>
  );
}
